import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { TopHeader } from './components/layout/AppShell';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import DeveloperDashboard from './pages/DeveloperDashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VirtualOfficePage from './pages/VirtualOfficePage';
import CandidateInterviewJoin from './pages/CandidateInterviewJoin';
import CertificateVerify from './pages/CertificateVerify';
import SharedInternship from './pages/SharedInternship';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { token, isInitializing } = useAuth();
  // On a hard reload, token starts as null for one render before AuthProvider's mount effect
  // restores it from localStorage. Redirecting to /login during that window would log an
  // already-logged-in user out on every refresh — wait for initialization to finish first.
  if (isInitializing) return null;
  return token ? children : <Navigate to="/login" />;
};

const AppContent: React.FC = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  // Mobile sidebar state lives here — the header's hamburger toggles it, and it's
  // handed down to whichever role dashboard is rendered so its sidebar becomes an
  // overlay (closes on nav/backdrop click) below the `lg` breakpoint.
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleDrawerClose = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* The role dashboards render their own sidebar + header (AppShell); every other
          signed-in page gets the compact 64px bar that VirtualOfficePage sizes itself against. */}
      {token && !location.pathname.startsWith('/dashboard') && (
        <TopHeader title="MQBIT HRMS" subtitle="MQBIT Technologies" compact />
      )}

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public candidate-facing join page — no login, reached only via the opaque link
            emailed by InterviewScheduling.tsx. Deliberately outside PrivateRoute. */}
        <Route path="/interview/:token" element={<CandidateInterviewJoin />} />
        {/* Public internship-certificate verification — the link/QR printed on the certificate.
            No login: ex-interns' accounts are closed, and employers/colleges have none. */}
        <Route path="/certificate/:token" element={<CertificateVerify />} />
        {/* Public read-only internship view for outside evaluators — opaque share link, no login. */}
        <Route path="/shared/internship/:token" element={<SharedInternship />} />
        <Route
          path="/dashboard/:view?"
          element={
            <PrivateRoute>
              {user?.role === 'Admin' ? (
                <AdminDashboard mobileOpen={mobileOpen} onDrawerClose={handleDrawerClose} onDrawerToggle={handleDrawerToggle} />
              ) : user?.role === 'Manager' ? (
                <ManagerDashboard mobileOpen={mobileOpen} onDrawerClose={handleDrawerClose} onDrawerToggle={handleDrawerToggle} />
              ) : (
                <DeveloperDashboard mobileOpen={mobileOpen} onDrawerClose={handleDrawerClose} onDrawerToggle={handleDrawerToggle} />
              )}
            </PrivateRoute>
          }
        />
        <Route
          path="/virtual-office"
          element={
            <PrivateRoute>
              <VirtualOfficePage />
            </PrivateRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
